import { Shul, PrayerType, DayType, ZmanimData, ResolvedScheduleTime, ScheduleEntry } from '../types';
import { resolveTimeRule } from './timeUtils';
import { PRAYER_TYPES } from '../config/constants';
import { entryPart, Level, NIGHT_OR_DAY, planPrayer, planSelichot, SelichotFrom } from './minyanDay';

type Match = { entry: ScheduleEntry; isOverride: boolean; overrideNote?: string; overrideNoteHe?: string };

function tagged(entry: ScheduleEntry, tags: DayType[]): boolean {
  return Array.isArray(entry.dayTypes) && entry.dayTypes.some((dt) => tags.includes(dt));
}

/**
 * Walk the levels (minyanDay.ts): the first level with a matching entry wins.
 * A `withWeekday` level replaces all weekday entries, unless each of its entries
 * names the one weekday entry it replaces; then the others are kept.
 * "No minyan" entries count as a match but show nothing.
 */
function pickByLevels(candidates: ScheduleEntry[], levels: Level[]): ScheduleEntry[] {
  for (const level of levels) {
    const hits = candidates.filter((e) => tagged(e, level.tags));
    if (hits.length === 0) continue;
    const shown = hits.filter((e) => !e.noMinyan);
    if (!level.withWeekday) return shown;
    // Own times replace all weekday times (Yoel, October 4, 2026), unless every
    // entry names the one weekday entry it replaces (old form, "Replaces").
    if (hits.some((e) => e.noMinyan || !e.overridesEntryId || e.overridesEntryId === '*')) return shown;
    const replaced = new Set(hits.map((e) => e.overridesEntryId).filter(Boolean));
    const ids = new Set(shown.map((e) => e.id));
    const weekday = candidates.filter(
      (e) => tagged(e, ['weekday']) && !e.noMinyan && !replaced.has(e.id) && !ids.has(e.id)
    );
    return [...shown, ...weekday];
  }
  return [];
}

/**
 * The entries a prayer shows on `today` (docs/day-types-spec.md section 1, step 4):
 * one-off entries for the date, then the levels for the day, then "this date
 * only" changes (a change replaces the time, `cancelled` hides it; an entry with
 * a change for this date shows even if its days don't match, as before).
 */
function findScheduleEntries(
  schedules: ScheduleEntry[],
  prayerType: PrayerType,
  today: string,
  defaultSelichotFrom: SelichotFrom
): Match[] {
  const active = schedules.filter((e) => e.prayerType === prayerType && e.active);

  if (prayerType === 'custom') {
    return active.filter((e) => e.specificDate === today).map((entry) => ({ entry, isOverride: false }));
  }

  let picked: ScheduleEntry[];
  if (prayerType === 'selichot') {
    const plan = planSelichot(today);
    const from = (e: ScheduleEntry): SelichotFrom => e.selichotFrom ?? defaultSelichotFrom;
    picked = [
      ...pickByLevels(active.filter((e) => from(e) === 'elul2'), plan.levels.elul2),
      ...pickByLevels(active.filter((e) => from(e) === 'firstNight'), plan.levels.firstNight),
    ];
  } else if (NIGHT_OR_DAY.includes(prayerType)) {
    const night = planPrayer(prayerType, today, 'start');
    const day = planPrayer(prayerType, today);
    picked = [
      ...pickByLevels(active.filter((e) => entryPart(e) === 'start'), night.part === 'start' ? night.levels : []),
      ...pickByLevels(active.filter((e) => entryPart(e) === 'day'), day.levels),
    ];
  } else {
    const plan = planPrayer(prayerType, today);
    picked = pickByLevels(active.filter((e) => entryPart(e) === plan.part), plan.levels);
  }

  // One-off extra minyan on a single date, for any prayer ("Change one date").
  picked = [...picked, ...active.filter((e) => e.specificDate === today && !e.noMinyan)];

  const results: Match[] = [];
  const pickedIds = new Set(picked.map((e) => e.id));
  for (const entry of active) {
    const change = entry.overrides?.find((o) => o.date === today);
    if (change) {
      if (!change.cancelled) {
        results.push({
          entry: { ...entry, timeRule: change.timeRule },
          isOverride: true,
          overrideNote: change.note,
          overrideNoteHe: change.noteHe,
        });
      }
    } else if (pickedIds.has(entry.id)) {
      results.push({ entry, isOverride: false });
    }
  }
  return results;
}

function toResolved(
  shul: Shul,
  prayerType: PrayerType,
  activeDayType: DayType,
  zmanim: ZmanimData,
  match: Match
): ResolvedScheduleTime {
  return {
    shulId: shul.id,
    shulNameEn: shul.nameEn,
    shulNameHe: shul.nameHe,
    scheduleId: match.entry.id,
    prayerType,
    displayTime: resolveTimeRule(match.entry.timeRule, zmanim),
    isOverride: match.isOverride,
    overrideNote: match.overrideNote,
    ...(match.overrideNoteHe ? { overrideNoteHe: match.overrideNoteHe } : {}),
    note: match.entry.note,
    noteHe: match.entry.noteHe,
    dayType: activeDayType,
  };
}

/** Resolve all of a shul's times for a given prayer type (may return multiple). */
export function resolveSchedule(
  shul: Shul,
  prayerType: PrayerType,
  today: string,
  activeDayType: DayType,
  zmanim: ZmanimData
): ResolvedScheduleTime[] {
  // Selichos start by the shul's nusach (Yoel, October 4, 2026): Edot HaMizrach and
  // Yemenite from 2 Elul; Ashkenaz, Sfarad and Other from the first Selichos night.
  const defaultSelichotFrom: SelichotFrom =
    shul.nusach === 'edotMizrach' || shul.nusach === 'yemenite' ? 'elul2' : 'firstNight';
  const matches = findScheduleEntries(shul.schedules || [], prayerType, today, defaultSelichotFrom);
  return matches
    .map((m) => toResolved(shul, prayerType, activeDayType, zmanim, m))
    .sort((a, b) => a.displayTime.localeCompare(b.displayTime));
}

/**
 * Resolve a shul's full schedule for the viewed day.
 * Ordered by PRAYER_TYPES, then by clock time within each prayer type.
 */
export function resolveShulSchedule(
  shul: Shul,
  today: string,
  activeDayType: DayType,
  zmanim: ZmanimData
): ResolvedScheduleTime[] {
  const results: ResolvedScheduleTime[] = [];
  for (const prayerType of PRAYER_TYPES) {
    results.push(...resolveSchedule(shul, prayerType, today, activeDayType, zmanim));
  }
  return results;
}

/** Resolve ALL schedules across ALL active shuls for a given day. */
export function resolveAllSchedules(
  shuls: Shul[],
  today: string,
  activeDayType: DayType,
  zmanim: ZmanimData
): ResolvedScheduleTime[] {
  const results: ResolvedScheduleTime[] = [];
  for (const shul of shuls) {
    if (!shul.active || shul.pendingApproval) continue;
    results.push(...resolveShulSchedule(shul, today, activeDayType, zmanim));
  }
  return results;
}
